import { ChainPositionBadge } from '../badges/ChainPositionBadge';
import { ConfidencePill } from '../badges/ConfidencePill';
import { ConflictAlertSeverityBadge } from '../badges/ConflictAlertSeverityBadge';
import { ConflictAlertTypeBadge } from '../badges/ConflictAlertTypeBadge';

import type { ConflictAlertVM } from '../../view-models';
import type React from 'react';

export interface ConflictAlertCardProps {
  alert: ConflictAlertVM;
  onOpenDrawer: (a: ConflictAlertVM) => void;
  /** Opcional — só usado quando `showActions` (lente operacional do supervisor). */
  onMitigate?: (a: ConflictAlertVM) => void;
  /** Opcional — só usado quando `showActions` (lente operacional do supervisor). */
  onAccept?: (a: ConflictAlertVM) => void;
  justDetected?: boolean;
  /**
   * Mostra o rodapé de ações (Mitigar / Aceitar). Default `true` (lente operacional
   * do supervisor — comportamento preservado). A lente forense do DPO (Centro de
   * conformidade) passa `false`: o DPO investiga, não age (não tem as caps de
   * `conflict-alert-mitigate`/`accept-with-justification`), então só "Ver detalhes".
   */
  showActions?: boolean;
}

// ─── ConflictAlertCard — anatomia §12.4 (verbatim de cards-3.jsx:369-455)
export function ConflictAlertCard({
  alert,
  onOpenDrawer,
  onMitigate,
  onAccept,
  justDetected,
  showActions = true,
}: ConflictAlertCardProps): React.JSX.Element {
  const isPaused = alert.conversation?.status === 'paused';
  const cardCls = [
    'ca-card',
    `ca-card--${alert.severity}`,
    justDetected && alert.severity === 'critical' && 'ca-card--pulse-once',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={cardCls} data-comment-anchor={`ca-card-${alert.id}`}>
      <header className="ca-card__head">
        <div className="ca-card__head-left">
          <ConflictAlertSeverityBadge severity={alert.severity} />
          <ConflictAlertTypeBadge alertType={alert.alertType} />
          {alert.reopened && (
            <span className="ca-card__reopened" title="Alerta reaberto de um estado terminal">
              ↺ Reaberto
            </span>
          )}
        </div>
        <div className="ca-card__head-right">
          <span className="ca-card__time mono">
            {alert.detectedAt} · há {alert.detectedAgo}
          </span>
        </div>
      </header>

      <h3 className="ca-card__title">{alert.title}</h3>

      {/* Parties */}
      <section className="ca-card__section">
        <div className="ca-card__section-label">Partes envolvidas</div>
        <div className="ca-card__parties">
          {alert.parties.map((p, i) => (
            <div key={i} className="ca-card__party">
              <span className="ca-card__party-avatar" aria-hidden="true">
                {p.name
                  .split(' ')
                  .map((s) => s[0])
                  .slice(0, 2)
                  .join('')}
              </span>
              <div className="ca-card__party-info">
                <div className="ca-card__party-name">
                  {p.name} <span className="ca-card__party-role">({p.role})</span>
                </div>
                <div className="ca-card__party-meta">
                  {p.kind === 'person' ? 'Pessoa' : 'Usuário'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contexto */}
      <section className="ca-card__section ca-card__section--grid">
        <div>
          <div className="ca-card__section-label">Conversa</div>
          <div className="ca-card__section-value">
            {alert.conversation.id !== '—' ? 'vinculada' : '—'}
            {isPaused && (
              <span className="ca-card__paused">
                {' '}
                · PAUSADA
                {alert.conversation.pausedSince != null
                  ? ` desde ${alert.conversation.pausedSince}`
                  : ''}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="ca-card__section-label">Detecção</div>
          <div className="ca-card__section-value mono" style={{ fontSize: 'var(--text-xs)' }}>
            {alert.agent}
          </div>
        </div>
        <div>
          <div className="ca-card__section-label">Confiança</div>
          <ConfidencePill value={alert.confidence} />
        </div>
        <div>
          <div className="ca-card__section-label">Reincidência</div>
          <ChainPositionBadge position={alert.chain.position} total={alert.chain.total} />
        </div>
      </section>

      {/* Impacto operacional */}
      {(alert.impact.pausedConversationIds.length > 0 ||
        alert.impact.blockedMcpToolCalls.length > 0) && (
        <section className="ca-card__section ca-card__section--impact">
          <div className="ca-card__section-label">
            Impacto operacional (cascata severidade alta+)
          </div>
          <div className="ca-card__impact-list mono">
            {alert.impact.pausedConversationIds.length > 0 && (
              <div>
                ⏸ <b>{alert.impact.pausedConversationIds.length}</b> conversa
                {alert.impact.pausedConversationIds.length === 1 ? '' : 's'} pausada
                {alert.impact.pausedConversationIds.length === 1 ? '' : 's'}
              </div>
            )}
            {alert.impact.blockedMcpToolCalls.length > 0 && (
              <div>
                🚫 <b>{alert.impact.blockedMcpToolCalls.length}</b> chamada
                {alert.impact.blockedMcpToolCalls.length === 1 ? '' : 's'} de ferramenta (MCP)
                bloqueada{alert.impact.blockedMcpToolCalls.length === 1 ? '' : 's'}
              </div>
            )}
          </div>
        </section>
      )}

      <footer className="ca-card__foot">
        <button className="btn-ghost-mini" onClick={() => onOpenDrawer(alert)}>
          Ver detalhes
        </button>
        {showActions && (
          <>
            <button
              className="btn-ghost-mini btn-ghost-mini--primary"
              onClick={() => onMitigate?.(alert)}
            >
              Mitigar com evidência
            </button>
            <button className="btn-ghost-mini" onClick={() => onAccept?.(alert)}>
              Aceitar com justificativa
            </button>
          </>
        )}
      </footer>
    </article>
  );
}
